import React, { useMemo, useState } from "react";
import { formatExactDate, formatRelativeTime } from "../Helper/dates";
import { levelIndicatorClass, levelLabel, logTagStyle } from "../Helper/logs";
import { useLogsData } from "../hooks/useLogsData";

const normalizeTags = (value: string) =>
	value
		.split(",")
		.map((tag) => tag.trim().toLowerCase())
		.filter((tag, index, arr) => tag.length > 0 && arr.indexOf(tag) === index);

const LEVEL_OPTIONS = [
	{ value: "all", label: "All levels" },
	{ value: "information", label: "Information" },
	{ value: "warning", label: "Warning" },
	{ value: "error", label: "Error" },
	{ value: "debug", label: "Debug" }
];

const LIMIT_OPTIONS = [50, 100, 150, 200];

/** Application log viewer with level, type, tag filtering and text search. */
const Logs: React.FC = () => {
	const [limit, setLimit] = useState(150);
	const [includeDebug, setIncludeDebug] = useState(true);
	const [levelFilter, setLevelFilter] = useState("all");
	const [typeFilter, setTypeFilter] = useState("");
	const [tagFilter, setTagFilter] = useState("");
	const [searchTerm, setSearchTerm] = useState("");

	const activeTags = useMemo(() => normalizeTags(tagFilter), [tagFilter]);

	const {
		logs,
		loading,
		error,
		lastUpdated,
		isRefreshing,
		refresh
	} = useLogsData({ limit, includeDebug, tags: activeTags });

	const filteredLogs = useMemo(() => {
		const normalizedLevel = levelFilter.toLowerCase();
		const normalizedType = typeFilter.trim().toLowerCase();
		const normalizedSearch = searchTerm.trim().toLowerCase();

		return logs.filter((entry) => {
			const entryLevel = (entry.level ?? "").toLowerCase();
			const entryType = (entry.type ?? "").toLowerCase();
			const entryText = `${entry.message ?? ""} ${entry.details ?? ""}`.toLowerCase();
			const entryTags = (entry.tags ?? []).map((tag) => (tag ?? "").toLowerCase());

			const matchesLevel = normalizedLevel === "all" ? true : entryLevel === normalizedLevel;
			const matchesType = normalizedType ? entryType.includes(normalizedType) : true;
			const matchesTags = activeTags.length === 0 ? true : activeTags.every((tag) => entryTags.includes(tag));
			const matchesSearch = normalizedSearch ? entryText.includes(normalizedSearch) : true;

			return matchesLevel && matchesType && matchesTags && matchesSearch;
		});
	}, [activeTags, levelFilter, logs, searchTerm, typeFilter]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Logs</p>
						<h1 className="text-2xl font-semibold text-slate-900">System activity</h1>
						{lastUpdated && (
							<p className="text-xs text-slate-500">
								Last fetch: {formatExactDate(lastUpdated)} ({formatRelativeTime(lastUpdated)})
							</p>
						)}
					</div>
					<button
						type="button"
						onClick={refresh}
						disabled={isRefreshing}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						{isRefreshing ? (
							<>
								<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
								Refreshing
							</>
						) : (
							"Refresh logs"
						)}
					</button>
				</div>

				<section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
					<header className="mb-6">
						<p className="text-sm font-medium uppercase tracking-wide text-slate-500">
							Filters
						</p>
					</header>
					<div className="grid gap-4 lg:grid-cols-4">
						<div className="space-y-2">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Level</label>
							<select
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={levelFilter}
								onChange={(event) => setLevelFilter(event.target.value)}
							>
								{LEVEL_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-2">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Type</label>
							<input
								type="text"
								placeholder="e.g. git:branch"
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={typeFilter}
								onChange={(event) => setTypeFilter(event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tags</label>
							<input
								type="text"
								placeholder="preview,languages"
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={tagFilter}
								onChange={(event) => setTagFilter(event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Search</label>
							<input
								type="text"
								placeholder="Message or details"
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								value={searchTerm}
								onChange={(event) => setSearchTerm(event.target.value)}
							/>
						</div>
					</div>
					<div className="mt-4 flex flex-wrap items-center gap-4">
						<label className="inline-flex items-center gap-2 text-sm text-slate-700">
							<input
								type="checkbox"
								className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
								checked={includeDebug}
								onChange={(event) => setIncludeDebug(event.target.checked)}
							/>
							<span>Include debug entries</span>
						</label>
						<label className="inline-flex items-center gap-2 text-sm text-slate-700">
							<span>Limit</span>
							<select
								className="rounded-lg border border-slate-200 px-3 py-1 text-sm focus:border-slate-500 focus:outline-none"
								value={limit}
								onChange={(event) => setLimit(Number(event.target.value))}
							>
								{LIMIT_OPTIONS.map((option) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</label>
						<button
							type="button"
							className="text-sm font-medium text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-900"
							onClick={() => {
								setLevelFilter("all");
								setTypeFilter("");
								setTagFilter("");
								setSearchTerm("");
							}}
						>
							Clear filters
						</button>
					</div>
				</section>

				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
						<div>
							<p className="text-sm font-medium uppercase tracking-wide text-slate-500">
								Log entries
							</p>
							<p className="text-sm text-slate-500">
								Showing {filteredLogs.length} of {logs.length} loaded entries
							</p>
						</div>
					</header>
					{error && (
						<div className="m-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
							{error}
						</div>
					)}
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
									<th className="px-5 py-3">Level</th>
									<th className="px-5 py-3">Message</th>
									<th className="px-5 py-3">Type</th>
									<th className="px-5 py-3">User</th>
									<th className="px-5 py-3">Created</th>
									<th className="px-5 py-3">Updated</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr>
										<td colSpan={6} className="px-5 py-6 text-center text-slate-500">
											Loading logs…
										</td>
									</tr>
								) : filteredLogs.length === 0 ? (
									<tr>
										<td colSpan={6} className="px-5 py-10 text-center text-slate-500">
											No logs match the current filters.
										</td>
									</tr>
								) : (
									filteredLogs.map((entry) => (
										<tr key={entry.id} className="hover:bg-slate-50">
											<td className="whitespace-nowrap px-5 py-3 font-semibold text-slate-900">
												<span className="inline-flex items-center gap-2">
													<span
														className={`inline-flex h-2.5 w-2.5 rounded-full ${levelIndicatorClass(entry.level)}`}
														title={entry.level || "Info"}
													/>
													{levelLabel(entry.level)}
												</span>
											</td>
											<td className="px-5 py-3 text-slate-700">
												<div className="flex gap-3">
													{entry.tags && entry.tags.length > 0 && (
														<div className="flex flex-wrap items-center gap-2 pt-0.5">
															{entry.tags.map((tag, index) => (
																<span
																	key={`${entry.id}-${tag}-${index}`}
																	className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${logTagStyle(
																		tag
																	)}`}
																>
																	{tag ?? "untagged"}
																</span>
															))}
														</div>
													)}
													<div className="flex-1 space-y-1">
														<p className="font-semibold text-slate-900">
															{entry.message || "No message"}
														</p>
														{entry.details && (
															<p className="text-xs text-slate-500">{entry.details}</p>
														)}
													</div>
												</div>
											</td>
											<td className="whitespace-nowrap px-5 py-3 text-slate-600">
												{entry.type || "—"}
											</td>
											<td className="whitespace-nowrap px-5 py-3 text-slate-600">
												{entry.user || "SYSTEM"}
											</td>
											<td className="whitespace-nowrap px-5 py-3 text-slate-500">
												<div className="text-xs font-semibold text-slate-900">
													{formatExactDate(entry.createdAt)}
												</div>
												<div className="text-[11px] uppercase tracking-wide text-slate-400">
													{formatRelativeTime(entry.createdAt)}
												</div>
											</td>
											<td className="whitespace-nowrap px-5 py-3 text-slate-500">
												<div className="text-xs font-semibold text-slate-900">
													{formatExactDate(entry.updatedAt)}
												</div>
												<div className="text-[11px] uppercase tracking-wide text-slate-400">
													{formatRelativeTime(entry.updatedAt)}
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>
			</div>
		</div>
	);
};

export default Logs;

